import React, { createContext, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import { colors, media, Theme } from '../../../core';
import ScrollingPanel from '../ScrollingPanel';
import Text from '../Text';

export interface StyledProps {
  variant?: 'primary' | 'secondary';
  active?: boolean;
  isSmall?: boolean;
  bottomLine?: string;
  disabled?: boolean;
}

export interface TabProps extends StyledProps {
  children: React.ReactNode;
  className?: string;
  id: string;
}

export interface TabsProps {
  children: React.ReactNode;
  className?: string;
  initialTab: string;
  onChange: (tabId: string, setActiveTab?: React.Dispatch<React.SetStateAction<string>>) => void;
  manualSetActiveTab?: boolean;
}

export interface PanelProps {
  children: React.ReactNode;
  className?: string;
  id: string;
  variant?: 'primary' | 'secondary';
}

export interface TabsContextProps {
  activeTab: string;
  handleTabClick?: (tabId: string) => void;
}

export const TabsContext = createContext<TabsContextProps>({
  activeTab: '1',
});

const StyledTabsBackground = styled.div<StyledProps>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  ${({ variant, theme }) =>
    variant === 'primary' &&
    `
    background-color: ${theme.tabsBackgroundColor || colors.riverstone};;
  `}
  ${({ variant }) =>
    variant === 'secondary' &&
    `
    background-color: ${colors.white};
  `}
`;

const StyledTabList = styled.div<StyledProps>`
  display: flex;
  flex-direction: row;
  ${({ variant, theme }) =>
    variant === 'primary' &&
    `
      height: ${theme.tabsHeight};
      background-color: ${theme.tabsBackgroundColor || colors.riverstone};
  `}
  ${({ variant, isSmall, theme }) =>
    variant === 'secondary' &&
    `
    display: flex;
    align-items: center;
    justify-content: ${isSmall ? 'flex-start' : `center`};

    ${media.tabletPortrait} {
      border-bottom: 2px solid ${theme.tabsBorderColor || colors.smoke};
    }
  `};
`;

const StyledTab = styled.button<StyledProps>`
  -webkit-appearance: none;
  border: 0;
  color: ${colors.ash};
  cursor: pointer;
  outline: 0;

  position: relative;
  white-space: nowrap;

  ${({ variant, theme }) =>
    variant === 'primary' &&
    `
      padding: 16px 20px;
      flex: 1;
      font-family: ${theme.fontFamily || `Quicksand, sans-serif`};
      background-color: ${theme.tabsBackgroundColor || colors.riverstone};
      font-size: ${theme.tabsFontSize};
      text-transform: uppercase;
      font-weight: ${theme.fontWeight || 600};
      line-height: 16px;
      position: relative;
      letter-spacing: ${theme.tabsLetterSpacing};

      &.active {
        background-color: ${colors.white};
        color: ${theme.tabsActiveColor || colors.charcoal};
        font-weight: ${theme.tabsActiveFontWeight};
      }

      &:hover:not(.active) {
        background-color: ${theme.tabsBackgroundColorHover || colors.pearl};
      }

      &:not(.active) + &:not(.active) {
        position: relative;

        &::before {
          content: '';
          display: block;
          position: absolute;
          left: 0;
          top: ${theme.tabsBorderTop};
          bottom: ${theme.tabsBorderBottom};
          border: 1px solid ${theme.tabsBorderColor || colors.smoke};
        }
      }
    `}

  ${({ theme, disabled }) =>
    theme.name === Theme.theme3 && disabled
      ? `
      background-color: ${theme.tabsDisabledBackgroundColor};
      cursor: not-allowed;
    `
      : ''}

  ${({ variant, isSmall, theme }) =>
    variant === 'secondary' &&
    `
      padding: ${isSmall ? '16px 32px 0 0' : 0};
      width:  ${isSmall ? 'auto' : '240px'};
      background-color: transparent;
      position: relative;

      &.active::after {
        content: '';
        position: ${isSmall ? `static` : `absolute`};
        display: block;
        left: 0;
        right: 0;
        width: 100%;
        height: ${isSmall ? 0 : '3px'};
        border-bottom: ${
          isSmall
            ? `2px solid ${theme.tabsActiveBorderColor || colors.primary}`
            : `3px solid ${theme.tabsActiveBorderColor || colors.primary}`
        };
        border-radius: ${isSmall ? `1px` : `1.5px`};
        z-index: 1;
      }

      &:not(.active){
        padding-bottom: ${isSmall ? '2px' : '0'};
        background-color: transparent;
      }

      &:focus {
        outline: 0
      }

      ${media.tabletPortrait} {
        padding: 0;
        padding-top: ${isSmall ? '16px' : 0};
        width: 100%;
        text-align: center;

        &.active::after {
          height: 2px;
          position: absolute;
          border-bottom: 2px solid ${theme.tabsActiveBorderColor || colors.primary};
        }

        &:not(.active){
          padding-bottom: 0;
        }
      }
    `}

  ${({ variant, bottomLine }) =>
    variant === 'primary' &&
    `
      border-bottom: ${bottomLine ? bottomLine : ''};
    `}
`;

const TabText = styled(Text)<StyledProps>`
  min-height: 24px;
  line-height: 16px;
  font-size: ${({ theme }) => theme.fontSize || '14px'};
  margin: 0;
  text-wrap: wrap;

  ${({ variant, theme, disabled }) =>
    variant === 'primary' &&
    `
      letter-spacing: 0.5px;
      text-transform: uppercase;

      ${
        theme.name === Theme.theme3 && disabled
          ? `
        color: ${theme.tabsDisabledColor};
        `
          : ''
      }
    `}

  ${({ variant, isSmall, theme }) =>
    variant === 'secondary' &&
    `
      font-weight: 600;
      padding: ${isSmall ? 0 : '24px 30px'};
      font-size: ${isSmall ? '14px' : '16px'};

      .active & {
        font-weight: bold;
        color: ${theme.tabsActiveColor || colors.charcoal};
      }
    `}
`;

const StyledPanel = styled.div<StyledProps>`
  background-color: ${colors.white};
  min-height: 100%;

  ${({ variant }) =>
    variant === 'primary' &&
    `
      padding: 30px;
  `}

  ${({ variant }) =>
    variant === 'secondary' &&
    `
    padding: 24px 0;
  `}
`;

const StyledNotification = styled.div`
  background-color: ${({ theme }) => theme.errorColor};
  border-radius: 50%;
  display: inline-block;
  height: 7px;
  vertical-align: middle;
  width: 7px;
`;

const IndicatorCircle = styled.div<{ color?: string }>`
  border-radius: 15px;
  background-color: ${({ color, theme }) => color || theme.errorColor}};
  display: inline-block;
  margin-left: 20px;
  padding: 0px 6px;
`;

const IndicatorText = styled(Text)`
  padding: 0;
  color: ${colors.white};
`;

const Tab = ({ children, className, id, variant, isSmall, bottomLine, disabled }: TabProps) => {
  const { activeTab, handleTabClick }: TabsContextProps = useContext(TabsContext);
  const isActive = activeTab === id;
  const classNames = (className || '') + (isActive ? ' active' : '');
  return (
    <StyledTab
      active={activeTab === id}
      className={classNames}
      onClick={() => handleTabClick && handleTabClick(id)}
      variant={variant}
      isSmall={isSmall}
      bottomLine={bottomLine}
      disabled={disabled}
    >
      <TabText variant={variant} isSmall={isSmall} disabled={disabled}>
        {children}
      </TabText>
    </StyledTab>
  );
};

const Indicator = ({ indicatorNumber, color }: { indicatorNumber: number; color?: string }) => (
  <IndicatorCircle color={color}>
    <IndicatorText size="xsmall" noMargin>
      {indicatorNumber <= 99 ? indicatorNumber : 99}
    </IndicatorText>
  </IndicatorCircle>
);

const Scrolling = ({ children, className, id, variant }: PanelProps) => {
  const { activeTab }: TabsContextProps = useContext(TabsContext);
  return activeTab === id ? (
    <ScrollingPanel>
      <StyledPanel className={className} variant={variant}>
        {children}
      </StyledPanel>
    </ScrollingPanel>
  ) : null;
};

const Panel = ({ children, className, id, variant }: PanelProps) => {
  const { activeTab }: TabsContextProps = useContext(TabsContext);

  return activeTab === id ? (
    <StyledPanel className={className} variant={variant}>
      {children}
    </StyledPanel>
  ) : null;
};

const Tabs = ({ children, className, initialTab, onChange, manualSetActiveTab }: TabsProps) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const handleTabClick = (tabId: string) => {
    if (tabId === activeTab) {
      return;
    }

    if (manualSetActiveTab) {
      onChange(tabId, setActiveTab);
    } else {
      setActiveTab(tabId);
      onChange(tabId);
    }
  };

  return (
    <TabsContext.Provider value={{ activeTab, handleTabClick }}>
      <StyledTabsBackground className={className}>{children}</StyledTabsBackground>
    </TabsContext.Provider>
  );
};

Tabs.TabList = StyledTabList;
Tabs.Tab = Tab;
Tabs.Panel = Panel;
Tabs.ScrollingPanel = Scrolling;
Tabs.Notification = StyledNotification;
Tabs.Indicator = Indicator;
Tabs.Context = TabsContext;

export default Tabs;
